import { COLORS, DISPLAY } from '../game/config';
import type { Camera, ObstacleState, SimulationState, Vec3 } from '../game/models';
import { OBSTACLE_MODELS, SKIMMER_MODEL, THREAT_MODELS, type WireModel } from './models';
import { ACTION_VIEWPORT, createGroundGrid, projectWorldSegment, worldToCamera, type ProjectedSegment } from './projection';

interface LineBatch {
  near: ProjectedSegment[];
  mid: ProjectedSegment[];
  far: ProjectedSegment[];
}

export class VectorRenderer {
  private readonly context: CanvasRenderingContext2D;
  private reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  private recordScore = 25_000;
  private hudAdaptation = 0;

  public constructor(private readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D is unavailable.');
    this.context = context;
  }

  public resize(): void {
    const cssWidth = this.canvas.getBoundingClientRect().width;
    this.hudAdaptation = cssWidth > 0 ? Math.max(0, Math.min(1, (960 - cssWidth) / 440)) : 0;
    this.canvas.dataset.hudLayout = cssWidth < 520 ? 'mobile' : cssWidth < 960 ? 'adaptive' : 'desktop';
    this.canvas.dataset.hudMainSize = String(18 + 18 * this.hudAdaptation);
    this.canvas.dataset.hudWarningSize = String(15 + 19 * this.hudAdaptation);
    const ratio = Math.min(window.devicePixelRatio || 1, DISPLAY.maxPixelRatio);
    const width = Math.round(DISPLAY.width * ratio);
    const height = Math.round(DISPLAY.height * ratio);
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
  }

  public render(scene: SimulationState): void {
    const context = this.context;
    const recoil = this.reducedMotion ? 0 : scene.feedback.recoil / 0.16;
    const bump = this.reducedMotion ? 0 : scene.feedback.bump / 0.14;
    const forward = { x: -Math.sin(scene.player.yaw), z: Math.cos(scene.player.yaw) };
    const camera: Camera = {
      position: {
        x: scene.player.position.x - forward.x * recoil * 0.18,
        y: DISPLAY.cameraHeight - bump * 0.09,
        z: scene.player.position.z - forward.z * recoil * 0.18,
      },
      yaw: scene.player.yaw + (scene.mode === 'attract' ? Math.sin(scene.elapsed * 0.05) * 0.24 : 0),
    };
    context.fillStyle = COLORS.background;
    context.fillRect(0, 0, DISPLAY.width, DISPLAY.height);
    this.drawCommandBand(scene.elapsed);

    context.save();
    context.beginPath();
    context.rect(ACTION_VIEWPORT.x, ACTION_VIEWPORT.y, ACTION_VIEWPORT.width, ACTION_VIEWPORT.height);
    context.clip();
    this.drawSky(camera, scene.elapsed);
    const batches = this.collectWorldLines(camera, scene.obstacles);
    this.drawLineBatches(batches);
    this.drawThreats(scene, camera);
    this.drawProjectiles(scene, camera);
    this.drawParticles(scene, camera);
    this.drawMuzzleFlash(scene);
    this.drawReticle(scene.elapsed);
    this.drawCockpit();
    context.restore();

    this.drawRadar(scene);
    this.drawHudText(scene);
    this.drawFeedbackOverlay(scene);
  }

  public setReducedMotion(reduced: boolean): void {
    this.reducedMotion = reduced;
  }

  public setRecordScore(score: number): void {
    this.recordScore = Math.max(0, Math.floor(score));
  }

  private drawCommandBand(elapsed: number): void {
    const context = this.context;
    const gradient = context.createLinearGradient(0, 0, 0, DISPLAY.hudHeight);
    gradient.addColorStop(0, '#150706');
    gradient.addColorStop(1, '#080706');
    context.fillStyle = gradient;
    context.fillRect(0, 0, DISPLAY.width, DISPLAY.hudHeight);
    context.strokeStyle = `rgba(255, 118, 87, ${0.42 + Math.sin(elapsed * 2) * 0.03})`;
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(0, DISPLAY.hudHeight - 0.5);
    context.lineTo(DISPLAY.width, DISPLAY.hudHeight - 0.5);
    context.stroke();
  }

  private drawSky(camera: Camera, elapsed: number): void {
    const context = this.context;
    const horizon = ACTION_VIEWPORT.y + ACTION_VIEWPORT.height * 0.49;
    context.fillStyle = 'rgba(125, 255, 157, 0.6)';
    for (let index = 0; index < 30; index += 1) {
      const x = ((index * 173 + camera.yaw * 190) % 1040 + 1040) % 1040 - 40;
      const y = ACTION_VIEWPORT.y + 28 + ((index * 67) % 170);
      const pulse = 0.35 + ((index * 13) % 8) / 12;
      context.globalAlpha = pulse;
      context.fillRect(x, y, 1.2, 1.2);
    }
    context.globalAlpha = 1;

    context.save();
    context.translate(754 - camera.yaw * 18, ACTION_VIEWPORT.y + 96);
    context.strokeStyle = 'rgba(125, 255, 157, 0.65)';
    context.lineWidth = 1.2;
    context.beginPath();
    context.arc(0, 0, 28, -1.2, 1.2);
    context.arc(10, 0, 24, 1.2, -1.2, true);
    context.stroke();
    context.restore();

    const offset = ((camera.yaw / (Math.PI * 2)) * DISPLAY.width * 2.4) % 190;
    const mountainPoints: Array<[number, number]> = [];
    for (let index = -2; index <= 24; index += 1) {
      const x = index * 48 - offset;
      const wave = Math.sin(index * 1.71) * 20 + Math.sin(index * 0.47) * 13;
      mountainPoints.push([x, horizon - 34 - Math.abs(wave)]);
    }
    this.strokePath(mountainPoints, 'rgba(86, 187, 112, 0.66)', 1.15, true);

    const volcanoX = ((477 - offset + 1100) % 1100) - 70;
    context.strokeStyle = 'rgba(125, 255, 157, 0.72)';
    context.beginPath();
    context.moveTo(volcanoX - 72, horizon);
    context.lineTo(volcanoX - 17, horizon - 62);
    context.lineTo(volcanoX + 18, horizon - 59);
    context.lineTo(volcanoX + 78, horizon);
    context.stroke();
    context.fillStyle = COLORS.command;
    if (!this.reducedMotion) {
      for (let spark = 0; spark < 4; spark += 1) {
        const phase = (elapsed * (0.22 + spark * 0.025) + spark * 0.21) % 1;
        context.globalAlpha = 0.7 * (1 - phase);
        context.fillRect(volcanoX + Math.sin(spark * 4.7) * phase * 19, horizon - 67 - phase * 32, 1.5, 1.5);
      }
    }
    context.globalAlpha = 1;
  }

  private collectWorldLines(camera: Camera, obstacles: ObstacleState[]): LineBatch {
    const batches: LineBatch = { near: [], mid: [], far: [] };
    const add = (start: Vec3, end: Vec3): void => {
      const line = projectWorldSegment(start, end, camera);
      if (!line) return;
      if (line.depth < 35) batches.near.push(line);
      else if (line.depth < 78) batches.mid.push(line);
      else batches.far.push(line);
    };

    for (const [start, end] of createGroundGrid(camera.position)) add(start, end);

    for (const obstacle of obstacles) {
      const model = OBSTACLE_MODELS[obstacle.kind];
      const sine = Math.sin(obstacle.yaw);
      const cosine = Math.cos(obstacle.yaw);
      const transformed = model.vertices.map((vertex) => ({
        x: obstacle.position.x + vertex.x * cosine - vertex.z * sine,
        y: obstacle.position.y + vertex.y,
        z: obstacle.position.z + vertex.x * sine + vertex.z * cosine,
      }));
      for (const [startIndex, endIndex] of model.edges) {
        const start = transformed[startIndex];
        const end = transformed[endIndex];
        if (start && end) add(start, end);
      }
    }
    return batches;
  }

  private drawLineBatches(batches: LineBatch): void {
    const definitions: ReadonlyArray<[ProjectedSegment[], number, number]> = [
      [batches.far, 0.3, 0.7],
      [batches.mid, 0.55, 1],
      [batches.near, 0.9, 1.25],
    ];
    for (const [lines, alpha, width] of definitions) {
      this.strokeSegments(lines, `rgba(77, 255, 125, ${alpha * 0.18})`, width * 5);
      this.strokeSegments(lines, `rgba(125, 255, 157, ${alpha})`, width);
    }
  }

  private strokeSegments(lines: ProjectedSegment[], color: string, width: number): void {
    if (lines.length === 0) return;
    const context = this.context;
    context.strokeStyle = color;
    context.lineWidth = width;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.beginPath();
    for (const line of lines) {
      context.moveTo(line.a.x, line.a.y);
      context.lineTo(line.b.x, line.b.y);
    }
    context.stroke();
  }

  private drawReticle(elapsed: number): void {
    const context = this.context;
    const x = DISPLAY.width / 2;
    const y = ACTION_VIEWPORT.y + ACTION_VIEWPORT.height * 0.48;
    context.strokeStyle = `rgba(216, 255, 225, ${0.82 + Math.sin(elapsed * 5) * 0.04})`;
    context.lineWidth = 1.2;
    context.beginPath();
    context.moveTo(x - 34, y);
    context.lineTo(x - 9, y);
    context.moveTo(x + 9, y);
    context.lineTo(x + 34, y);
    context.moveTo(x, y - 25);
    context.lineTo(x, y - 8);
    context.moveTo(x, y + 8);
    context.lineTo(x, y + 25);
    context.stroke();
  }

  private drawCockpit(): void {
    const context = this.context;
    context.strokeStyle = 'rgba(79, 207, 111, 0.62)';
    context.lineWidth = 1.4;
    context.beginPath();
    context.moveTo(0, DISPLAY.height - 44);
    context.lineTo(164, DISPLAY.height - 75);
    context.lineTo(303, DISPLAY.height - 58);
    context.lineTo(356, DISPLAY.height);
    context.moveTo(DISPLAY.width, DISPLAY.height - 44);
    context.lineTo(796, DISPLAY.height - 75);
    context.lineTo(657, DISPLAY.height - 58);
    context.lineTo(604, DISPLAY.height);
    context.stroke();
  }

  private drawRadar(scene: SimulationState): void {
    const context = this.context;
    const centerX = DISPLAY.width / 2;
    const centerY = 68;
    const radius = 48 + 20 * this.hudAdaptation;
    context.strokeStyle = 'rgba(255, 118, 87, 0.72)';
    context.lineWidth = 1 + this.hudAdaptation;
    context.beginPath();
    context.arc(centerX, centerY, radius, 0, Math.PI * 2);
    context.arc(centerX, centerY, radius * 0.52, 0, Math.PI * 2);
    context.moveTo(centerX, centerY - 5);
    context.lineTo(centerX - 7, centerY + 8);
    context.lineTo(centerX + 7, centerY + 8);
    context.closePath();
    context.stroke();

    const sweepAngle = scene.radar.sweepAngle;
    context.strokeStyle = 'rgba(255, 208, 180, 0.85)';
    context.beginPath();
    context.moveTo(centerX, centerY);
    context.lineTo(centerX + Math.sin(sweepAngle) * radius, centerY - Math.cos(sweepAngle) * radius);
    context.stroke();

    for (const blip of scene.radar.blips) {
      const scale = radius / 100;
      const rx = Math.max(-radius + 3, Math.min(radius - 3, blip.relativeX * scale));
      const rz = Math.max(-radius + 3, Math.min(radius - 3, blip.relativeZ * scale));
      if (rx * rx + rz * rz >= radius * radius || blip.intensity <= 0) continue;
      context.globalAlpha = blip.kind === 'threat' ? blip.intensity : blip.intensity * 0.58;
      context.strokeStyle = blip.kind === 'threat' ? COLORS.commandHot : COLORS.command;
      context.fillStyle = COLORS.command;
      if (blip.kind === 'threat') {
        const blipRadius = 3 + 2 * this.hudAdaptation;
        context.lineWidth = 1.5 + this.hudAdaptation;
        context.strokeRect(centerX + rx - blipRadius, centerY - rz - blipRadius, blipRadius * 2, blipRadius * 2);
      } else {
        const blipSize = 2 + 2 * this.hudAdaptation;
        context.fillRect(centerX + rx - blipSize / 2, centerY - rz - blipSize / 2, blipSize, blipSize);
      }
    }
    context.globalAlpha = 1;

    const halfFov = DISPLAY.horizontalFovRadians / 2;
    context.strokeStyle = 'rgba(255, 118, 87, 0.36)';
    context.beginPath();
    context.moveTo(centerX, centerY);
    context.lineTo(centerX - Math.sin(halfFov) * radius, centerY - Math.cos(halfFov) * radius);
    context.moveTo(centerX, centerY);
    context.lineTo(centerX + Math.sin(halfFov) * radius, centerY - Math.cos(halfFov) * radius);
    context.stroke();
  }

  private drawHudText(scene: SimulationState): void {
    const context = this.context;
    const mainSize = 18 + 18 * this.hudAdaptation;
    const sideInset = 44 - 22 * this.hudAdaptation;
    context.font = `${mainSize}px ui-monospace, SFMono-Regular, Menlo, monospace`;
    context.fillStyle = COLORS.commandHot;
    context.textBaseline = 'middle';
    context.fillText(`SCORE  ${String(scene.score).padStart(6, '0').slice(-8)}`, sideInset, 50 - 8 * this.hudAdaptation);
    context.fillText(`RECORD ${String(this.recordScore).padStart(6, '0').slice(-8)}`, sideInset, 83 + 15 * this.hudAdaptation);
    context.textAlign = 'right';
    const reserve = '◇ '.repeat(Math.max(0, Math.min(5, scene.tanks - 1))).trim();
    context.fillText(`RESERVE  ${reserve || '—'}`, DISPLAY.width - sideInset, 50 - 8 * this.hudAdaptation);
    context.fillStyle = `rgba(255, 118, 87, ${0.72 + Math.sin(scene.elapsed * 2.5) * 0.12})`;
    context.fillText(scene.mode === 'paused' ? 'SYSTEM PAUSED' : 'RADAR SWEEP', DISPLAY.width - sideInset, 83 + 15 * this.hudAdaptation);
    context.textAlign = 'start';

    const warning = this.threatWarning(scene);
    if (warning) {
      context.textAlign = 'center';
      context.font = `${15 + 19 * this.hudAdaptation}px ui-monospace, SFMono-Regular, Menlo, monospace`;
      context.fillStyle = COLORS.command;
      context.fillText(warning, DISPLAY.width / 2, 119 + 43 * this.hudAdaptation);
      context.textAlign = 'start';
    }

    if (scene.mode === 'ready' || scene.mode === 'life-lost' || scene.mode === 'game-over') {
      const labels = {
        ready: `TREADS ONLINE  ${Math.max(1, Math.ceil(1.6 - scene.modeTime))}`,
        'life-lost': 'ARMOR BREACH',
        'game-over': 'MISSION ENDED',
      } as const;
      context.textAlign = 'center';
      context.font = `${30 + 14 * this.hudAdaptation}px ui-monospace, SFMono-Regular, Menlo, monospace`;
      context.fillStyle = scene.mode === 'life-lost' ? COLORS.commandHot : COLORS.phosphorHot;
      context.fillText(labels[scene.mode], DISPLAY.width / 2, DISPLAY.hudHeight + 74);
      context.textAlign = 'start';
    }
  }

  private threatWarning(scene: SimulationState): string {
    const threat = scene.primaryThreat;
    if (!threat) return '';
    if (threat.kind === 'missile') return 'MISSILE INBOUND';
    const relative = worldToCamera(threat.position, {
      position: { x: scene.player.position.x, y: 0, z: scene.player.position.z },
      yaw: scene.player.yaw,
    });
    if (relative.z < 0) return 'CONTACT REAR';
    if (Math.abs(relative.x) < relative.z * 0.35) return threat.aiMode === 'aim' ? 'ENEMY FIRING' : 'TARGET AHEAD';
    return relative.x < 0 ? 'CONTACT LEFT' : 'CONTACT RIGHT';
  }

  private drawMuzzleFlash(scene: SimulationState): void {
    if (scene.feedback.muzzleFlash <= 0) return;
    const context = this.context;
    const amount = scene.feedback.muzzleFlash / 0.12;
    const x = DISPLAY.width / 2;
    const y = ACTION_VIEWPORT.y + ACTION_VIEWPORT.height * 0.48;
    context.globalAlpha = this.reducedMotion ? Math.min(0.45, amount) : amount * 0.8;
    context.fillStyle = COLORS.phosphorHot;
    context.beginPath();
    context.arc(x, y, this.reducedMotion ? 4 : 7 + amount * 15, 0, Math.PI * 2);
    context.fill();
    context.globalAlpha = 1;
  }

  private drawFeedbackOverlay(scene: SimulationState): void {
    const context = this.context;
    if (scene.feedback.messageTime > 0 && scene.feedback.message) {
      context.textAlign = 'center';
      context.font = `${20 + 14 * this.hudAdaptation}px ui-monospace, SFMono-Regular, Menlo, monospace`;
      context.fillStyle = scene.feedback.bonusFlash > 0 ? COLORS.commandHot : COLORS.phosphorHot;
      context.globalAlpha = Math.min(1, scene.feedback.messageTime * 1.6);
      context.fillText(scene.feedback.message, DISPLAY.width / 2, DISPLAY.height - 104);
      context.globalAlpha = 1;
      context.textAlign = 'start';
    }
    if (scene.feedback.damageFlash > 0) {
      context.fillStyle = `rgba(255, 118, 87, ${this.reducedMotion ? Math.min(0.12, scene.feedback.damageFlash * 0.12) : scene.feedback.damageFlash * 0.34})`;
      context.fillRect(0, DISPLAY.hudHeight, DISPLAY.width, DISPLAY.height - DISPLAY.hudHeight);
    }
    if (scene.feedback.bonusFlash > 0) {
      context.strokeStyle = `rgba(255, 208, 180, ${scene.feedback.bonusFlash * 0.7})`;
      context.lineWidth = 2;
      context.strokeRect(8, 8, DISPLAY.width - 16, DISPLAY.height - 16);
    }
  }

  private drawThreats(scene: SimulationState, camera: Camera): void {
    if (scene.primaryThreat) {
      const lines = this.projectModel(
        THREAT_MODELS[scene.primaryThreat.kind],
        scene.primaryThreat.position,
        scene.primaryThreat.yaw,
        camera,
      );
      const aiming = scene.primaryThreat.aiMode === 'aim';
      const glow = aiming ? 'rgba(255, 118, 87, 0.25)' : 'rgba(125, 255, 157, 0.2)';
      const core = aiming ? 'rgba(255, 208, 180, 0.96)' : 'rgba(180, 255, 197, 0.92)';
      this.strokeSegments(lines, glow, aiming ? 8 : 6);
      this.strokeSegments(lines, core, aiming ? 1.8 : 1.35);
    }
    if (scene.skimmer) {
      const lines = this.projectModel(SKIMMER_MODEL, scene.skimmer.position, scene.elapsed * 0.7, camera);
      this.strokeSegments(lines, 'rgba(255, 118, 87, 0.2)', 7);
      this.strokeSegments(lines, 'rgba(255, 208, 180, 0.95)', 1.45);
    }
  }

  private projectModel(model: WireModel, position: Vec3, yaw: number, camera: Camera): ProjectedSegment[] {
    const sine = Math.sin(yaw);
    const cosine = Math.cos(yaw);
    const vertices = model.vertices.map((vertex) => ({
      x: position.x + vertex.x * cosine - vertex.z * sine,
      y: position.y + vertex.y,
      z: position.z + vertex.x * sine + vertex.z * cosine,
    }));
    const lines: ProjectedSegment[] = [];
    for (const [startIndex, endIndex] of model.edges) {
      const start = vertices[startIndex];
      const end = vertices[endIndex];
      if (!start || !end) continue;
      const line = projectWorldSegment(start, end, camera);
      if (line) lines.push(line);
    }
    return lines;
  }

  private drawProjectiles(scene: SimulationState, camera: Camera): void {
    const lines: ProjectedSegment[] = [];
    for (const projectile of scene.projectiles) {
      const back = {
        x: projectile.position.x - projectile.velocity.x * 0.045,
        y: projectile.position.y - projectile.velocity.y * 0.045,
        z: projectile.position.z - projectile.velocity.z * 0.045,
      };
      const line = projectWorldSegment(back, projectile.position, camera);
      if (line) lines.push(line);
    }
    this.strokeSegments(lines, 'rgba(216, 255, 225, 0.2)', 7);
    this.strokeSegments(lines, 'rgba(216, 255, 225, 1)', 2.1);
  }

  private drawParticles(scene: SimulationState, camera: Camera): void {
    const context = this.context;
    const particles = this.reducedMotion
      ? scene.particles.filter((_particle, index) => index % 4 === 0)
      : scene.particles;
    for (const particle of particles) {
      const tail = {
        x: particle.position.x - particle.velocity.x * (this.reducedMotion ? 0.012 : 0.045),
        y: particle.position.y - particle.velocity.y * (this.reducedMotion ? 0.012 : 0.045),
        z: particle.position.z - particle.velocity.z * (this.reducedMotion ? 0.012 : 0.045),
      };
      const line = projectWorldSegment(tail, particle.position, camera);
      if (!line) continue;
      context.globalAlpha = Math.max(0, particle.life / particle.maxLife);
      context.strokeStyle = COLORS.commandHot;
      context.lineWidth = 1.1;
      context.beginPath();
      context.moveTo(line.a.x, line.a.y);
      context.lineTo(line.b.x, line.b.y);
      context.stroke();
    }
    context.globalAlpha = 1;
  }

  private strokePath(points: Array<[number, number]>, color: string, width: number, glow: boolean): void {
    if (points.length < 2) return;
    const context = this.context;
    const draw = (strokeStyle: string, lineWidth: number): void => {
      context.strokeStyle = strokeStyle;
      context.lineWidth = lineWidth;
      context.beginPath();
      const first = points[0];
      if (!first) return;
      context.moveTo(first[0], first[1]);
      for (const point of points.slice(1)) context.lineTo(point[0], point[1]);
      context.stroke();
    };
    if (glow) draw('rgba(65, 255, 116, 0.12)', width * 5);
    draw(color, width);
  }
}
